import { useMemo, useRef, useState } from "preact/hooks";

type Options<T> = {
	readonly key: string;
	readonly defaultValue: T;
	readonly parse: (raw: string) => T;
	readonly stringify: (value: T) => string | undefined;
};

function useLocalStorage<T>({
	key,
	defaultValue,
	parse,
	stringify,
}: Options<T>): Readonly<[T, (value: T) => void]> {
	const [value, setValue] = useState<T>(() => {
		const rawValue = localStorage.getItem(key);

		// Not yet stored
		if (rawValue === null) {
			return defaultValue;
		}

		// Try and parse
		try {
			return parse(rawValue);
		} catch (e) {
			console.error(`Error parsing local storage ${key} value ${rawValue}`, e);
			return defaultValue;
		}
	});

	const defaultValueRef = useRef(defaultValue);
	defaultValueRef.current = defaultValue;
	const stringifyRef = useRef(stringify);
	stringifyRef.current = stringify;
	return useMemo(
		() => [
			value,
			(newValue: T) => {
				setValue(newValue);

				if (newValue === defaultValueRef.current) {
					localStorage.removeItem(key);
					return;
				}

				const newStringified = stringify(newValue);
				if (newStringified === undefined) {
					localStorage.removeItem(key);
				} else {
					localStorage.setItem(key, newStringified);
				}
			},
		],
		[value, key, stringifyRef, defaultValueRef],
	);
}

export default useLocalStorage;
